<script setup lang="ts">
import { computed, ref } from 'vue';
import BackstageIcon from './BackstageIcon.vue';
import BackstageInputLabel from './BackstageInputLabel.vue';
import { Icons } from '@/models/icons.enum';
import { Field as VeeField, ErrorMessage } from 'vee-validate';

interface Props {
  name?: string;
  label?: string;
  placeholder?: string;
  modelValue?: string | number;
  readOnlyValue?: any;
  disabled?: boolean;
  readOnly?: boolean;
  type?: string;
  tabIndex?: string;
  autocomplete?: string;
  required?: boolean;
  dataCyInput?: string;
  maxChars?: number;
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  placeholder: '',
  modelValue: '',
  readOnlyValue: '',
  disabled: false,
  readOnly: false,
  type: 'text',
  required: false,
  dataCyInput: '',
});

const emit = defineEmits<{
  change: [];
  'update:modelValue': [value: string | number];
}>();

const showPassword = ref(false);
const inputFocused = ref(false);
const input = ref<HTMLInputElement | null>(null);

// computed properties
const computedType = computed<string>(() => {
  if (props.type === 'password') {
    return showPassword.value ? 'text' : 'password';
  }

  return props.type;
});

const showPasswordIcon = computed<boolean>(() => {
  return props.type === 'password';
});

const passwordIconName = computed<Icons>(() => {
  return showPassword.value ? Icons.EYE_OPEN : Icons.EYE_CLOSED;
});

const labelIsPlaceHolder = computed<boolean>(() => {
  return props.placeholder === '' && !props.modelValue && !inputFocused.value && !props.readOnly && !props.disabled;
});

// methods
function onPasswordIconClick() {
  showPassword.value = !showPassword.value;
}

function focusOnInput() {
  inputFocused.value = true;
  input.value?.focus();
}

function removeInputFocus() {
  inputFocused.value = false;
  emit('change');
}

function updateValue(event: Event) {
  const input = event.target as HTMLInputElement;
  let inputValue = input.value;

  if (props.maxChars && inputValue.length > props.maxChars) {
    inputValue = inputValue.slice(0, props.maxChars);
  }

  emit('update:modelValue', inputValue);
}
</script>

<template>
  <span>
    <BackstageInputLabel
      :class="[{ placeholder: labelIsPlaceHolder, disabled: disabled, 'read-only': readOnly }]"
      :label="label"
      :label-for="name"
      :required="required"
      v-on:focus="focusOnInput"
      v-on:click="focusOnInput">
      <template #tooltip>
        <slot name="tooltip"></slot>
      </template>
      <VeeField v-if="name" :name="name" v-slot="{ field }" :model-value="modelValue" :validate-on-input="true">
        <textarea
          v-if="type === 'textarea'"
          v-bind="field"
          ref="input"
          :id="name"
          :name="name"
          :data-cy="dataCyInput"
          :class="{ hidden: labelIsPlaceHolder }"
          :disabled="disabled || readOnly"
          :placeholder="placeholder"
          :value="modelValue"
          :type="computedType"
          :tabindex="tabIndex"
          :autocomplete="autocomplete"
          v-on:blur="removeInputFocus"
          v-on:input="updateValue">
        </textarea>
        <input
          v-else
          ref="input"
          :id="name"
          v-bind="{ ...$attrs, ...field }"
          :name="name"
          :data-cy="dataCyInput"
          :class="{ hidden: labelIsPlaceHolder }"
          :disabled="disabled || readOnly"
          :placeholder="placeholder"
          :value="modelValue"
          :type="computedType"
          :tabindex="tabIndex"
          :autocomplete="autocomplete"
          v-on:blur="removeInputFocus"
          v-on:input="updateValue" />
      </VeeField>
      <span v-else-if="readOnly">{{ readOnlyValue ?? modelValue }}</span>
      <input
        v-else
        v-bind="$attrs"
        ref="input"
        :name="name"
        :data-cy="dataCyInput"
        :class="{ hidden: labelIsPlaceHolder }"
        :disabled="disabled || readOnly"
        :placeholder="placeholder"
        :value="modelValue"
        :type="computedType"
        :tabindex="tabIndex"
        :autocomplete="autocomplete"
        v-on:blur="removeInputFocus"
        v-on:input="updateValue" />
      <BackstageIcon
        v-if="showPasswordIcon"
        :name="passwordIconName"
        class="password-icon"
        data-cy="password-icon"
        v-on:click="onPasswordIconClick" />
    </BackstageInputLabel>
    <ErrorMessage v-if="name" :name="name" />
  </span>
</template>
